import Box from "@mui/material/Box";
import {
  MutableRefObject,
  RefObject,
  useEffect,
  useMemo,
  useState,
} from "react";

import { Comparison } from "./APComparison";

type APComparisonCriteriaHighlightProps<T> = {
  comparison: Comparison<T>;
  expandedCriteria: Record<string, boolean>;
  sortCriteria: keyof T;
  resetScroll: () => void;
  labelRefs: MutableRefObject<Record<string, HTMLElement>>;
  scrollRef: RefObject<HTMLDivElement>;
  zIndex: number;
};

const APComparisonCriteriaHighlight = <T,>({
  comparison,
  expandedCriteria,
  sortCriteria,
  resetScroll,
  labelRefs,
  scrollRef,
  zIndex,
}: APComparisonCriteriaHighlightProps<T>) => {
  // Add state for the floating highlight
  const [highlightPosition, setHighlightPosition] = useState<{
    top: number;
    height: number;
    criterion: keyof T;
  } | null>(null);

  // Function to update highlight position
  const updateHighlightPosition = (criteriaId: keyof T) => {
    const labelElement = labelRefs.current[String(criteriaId)];
    if (labelElement) {
      const rect = labelElement.getBoundingClientRect();
      const containerRect = scrollRef.current?.getBoundingClientRect();

      if (containerRect && rect.top > containerRect.top) {
        setHighlightPosition({
          top: rect.top - containerRect.top - 1,
          height: rect.height + 1,
          criterion: criteriaId,
        });
      }
    }
  };

  const isSortCriterionHidden = useMemo(() => {
    const expandableCriterion = comparison.expandableCriteria.find(
      (expandableCriterion) =>
        expandableCriterion.criteria.includes(sortCriteria)
    );
    const isSelectedCriterion =
      comparison.selectedCriteria.includes(sortCriteria);
    const isInitialPrimaryCriteria =
      sortCriteria === comparison.initialPrimaryCriteria;
    const isVisible =
      isSelectedCriterion ||
      isInitialPrimaryCriteria ||
      !expandableCriterion ||
      expandedCriteria[expandableCriterion.id];

    if (expandableCriterion && !isVisible) {
      updateHighlightPosition(expandableCriterion.criteria[0]);
    }

    return !isVisible;
  }, [comparison.expandableCriteria, sortCriteria, expandedCriteria]);

  useEffect(() => {
    updateHighlightPosition(sortCriteria);
  }, [expandedCriteria]);

  useEffect(() => {
    if (sortCriteria) {
      resetScroll();
      updateHighlightPosition(sortCriteria);

      window.addEventListener("resize", () =>
        updateHighlightPosition(sortCriteria)
      );
      return () =>
        window.removeEventListener("resize", () =>
          updateHighlightPosition(sortCriteria)
        );
    }
  }, [sortCriteria]);

  return (
    <>
      {highlightPosition && !isSortCriterionHidden && (
        <Box
          sx={{
            position: "absolute",
            left: -6,
            top: `${highlightPosition.top}px`,
            right: -6,
            height: `${highlightPosition.height}px`,
            borderRadius: "8px",
            zIndex,
            transition: "all 0.3s ease-in-out",
            pointerEvents: "none",
            border: "3px solid",
            borderColor: "secondary.main",
          }}
        />
      )}
    </>
  );
};

export default APComparisonCriteriaHighlight;
